import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts a timestamptz string to a relative time format
 * @param timestamp - ISO 8601 timestamp string
 * @param options - Optional configuration
 * @returns Relative time string (e.g., "less than a minute ago", "2 hours ago")
 */
export function formatRelativeTime(
  timestamp: string,
  options: {
    includeSeconds?: boolean;
    addSuffix?: boolean;
    threshold?: number; // milliseconds threshold for "less than X ago"
  } = {}
): string {
  const {
    includeSeconds = false,
    addSuffix = true,
    threshold = 60000 // 1 minute default threshold
  } = options;

  const now = new Date();
  const date = new Date(timestamp);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  // Handle future dates
  if (diffInMs < 0) {
    const absDiffInMs = Math.abs(diffInMs);
    if (absDiffInMs < threshold) {
      return "in less than a minute";
    }
    if (diffInSeconds < 60) {
      return `in ${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''}`;
    }
    if (diffInMinutes < 60) {
      return `in ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`;
    }
    if (diffInHours < 24) {
      return `in ${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`;
    }
    if (diffInDays < 7) {
      return `in ${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
    }
    if (diffInWeeks < 4) {
      return `in ${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''}`;
    }
    if (diffInMonths < 12) {
      return `in ${diffInMonths} month${diffInMonths !== 1 ? 's' : ''}`;
    }
    return `in ${diffInYears} year${diffInYears !== 1 ? 's' : ''}`;
  }

  // Handle past dates
  if (diffInMs < threshold) {
    return "less than a minute ago";
  }
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
  }
  
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }
  
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }
  
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  }
  
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
  }
  
  return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
}

/**
 * Formats a timestamptz string to a readable date format
 * @param timestamp - ISO 8601 timestamp string
 * @param options - Date formatting options
 * @returns Formatted date string
 */
export function formatDate(
  timestamp: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }
): string {
  const date = new Date(timestamp);
  
  if (isNaN(date.getTime())) {
    return "Invalid date";
  }
  
  return date.toLocaleDateString('en-US', options);
}

/**
 * Formats a timestamptz string to include both date and time
 * @param timestamp - ISO 8601 timestamp string
 * @returns Formatted date and time string
 */
export function formatDateTime(timestamp: string): string {
  const date = new Date(timestamp);
  
  if (isNaN(date.getTime())) {
    return "Invalid date";
  }
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Convert tag IDs to tag names using available tags data
 * @param tagIds - Array of tag IDss
 * @param availableTags - Array of available tag objects
 * @returns Array of tag names
 */
export function convertTagIdsToNames(tagIds: string[] | string | null | undefined, availableTags: Array<{ id: string; name: string }>): string[] {
  
  // Handle different input types
  if (!tagIds) {
    return [];
  }
  if (typeof tagIds === 'string') {
    // If it's a single string, treat it as a single tag ID
    const tag = availableTags.find(t => t.id === tagIds);
    return tag ? [tag.name] : [tagIds];
  }
  
  if (!Array.isArray(tagIds)) {
    console.warn('convertTagIdsToNames: tagIds is not an array, converting to empty array', tagIds);
    return [];
  }
  
  return tagIds.map(tagId => {
    const tag = availableTags.find(t => t.id === tagId);
    return tag ? tag.name : tagId; // Fallback to ID if tag not found
  });
}

/**
 * Convert tag IDs to tag objects (with color information) using available tags data
 * @param tagIds - Array of tag IDs
 * @param availableTags - Array of available tag objects
 * @returns Array of tag objects with id, name, and color
 */
export function convertTagIdsToObjects(tagIds: string[] | string | null | undefined, availableTags: Array<{ id: string; name: string; color?: string }>): Array<{ id: string; name: string; color?: string }> {
  
  // Handle different input types
  if (!tagIds) {
    return [];
  }
  
  if (typeof tagIds === 'string') {
    // If it's a single string, treat it as a single tag ID
    const tag = availableTags.find(t => t.id === tagIds);
    return tag ? [{ id: tag.id, name: tag.name, color: tag.color }] : [{ id: tagIds, name: tagIds }];
  }
  
  if (!Array.isArray(tagIds)) {
    console.warn('convertTagIdsToObjects: tagIds is not an array, converting to empty array', tagIds);
    return [];
  }
  return tagIds.map(tagId => {
    const tag = availableTags.find(t => t.id === tagId);
    return tag ? { id: tag.id, name: tag.name, color: tag.color } : { id: tagId, name: tagId };
  });
}

/**
 * Convert tag names to tag IDs using available tags data
 * @param tagNames - Array of tag names
 * @param availableTags - Array of available tag objects
 * @returns Array of tag IDs
 */
export function convertTagNamesToIds(tagNames: string[] | string | null | undefined, availableTags: Array<{ id: string; name: string }>): string[] {
  // Handle different input types
  if (!tagNames) {
    return [];
  }
  
  if (typeof tagNames === 'string') {
    // If it's a single string, treat it as a single tag name
    const tag = availableTags.find(t => t.name === tagNames);
    return tag ? [tag.id] : [tagNames];
  }
  
  if (!Array.isArray(tagNames)) {
    console.warn('convertTagNamesToIds: tagNames is not an array, converting to empty array', tagNames);
    return [];
  }
  
  return tagNames.map(tagName => {
    const tag = availableTags.find(t => t.name === tagName);
    return tag ? tag.id : tagName; // Fallback to name if tag not found
  });
}

/**
 * Utility function to increment version numbers precisely
 * Avoids floating point precision issues by converting to integers
 */
export const incrementVersion = (currentVersion: number): number => {
  // Convert to integer to avoid floating point precision issues
  const versionAsInteger = Math.round(currentVersion * 100000);
  const newVersionAsInteger = versionAsInteger + 1;
  return newVersionAsInteger / 100000;
};

/**
 * Test function to verify version incrementing works correctly
 */
export const testVersionIncrement = () => {
  let version = 0.00001;
  console.log('Testing version increments:');
  
  for (let i = 0; i < 10; i++) {
    console.log(`Version ${i + 1}: ${version}`);
    version = incrementVersion(version);
  }
  
  // Test that we get exactly 0.00011 after 10 increments
  console.log(`Final version should be 0.00011: ${version === 0.00011 ? 'PASS' : 'FAIL'} (${version})`);
};

// Re-export page config loader utilities
export { loadPageConfigFromJson, usePageConfigLoader } from './utils/pageConfigLoader';

/**
 * Converts a GlossaryBlueprint data structure to form sections and developer notes
 * @param blueprint - The glossary blueprint to convert
 * @returns Object containing sections, developerNotes, and renderOrder
 */
export function convertBlueprintToForm(blueprint: {
  data: Record<string, unknown>;
}) {
  console.log("convertBlueprintToForm: Starting with blueprint data:", blueprint.data);
  
  const sections: Array<{
    id: string;
    title: string;
    fields: Array<{
      id: string;
      type: 'text' | 'number' | 'image' | 'glossary-item' | 'tags' | 'types';
      label: string;
      mandatory: boolean;
      translation: boolean;
      selectedTypes?: string[];
    }>;
  }> = [];
  
  const developerNotes: Array<{
    id: string;
    message: string;
    color: 'amber' | 'blue' | 'green' | 'red' | 'purple' | 'gray';
  }> = [];
  
  const renderOrder: Array<{ type: 'section' | 'note'; id: string }> = [];

  if (blueprint.data && typeof blueprint.data === 'object') {
    // Handle new structure with sections, devnotes, and elementOrder
    if (
      'sections' in blueprint.data &&
      'devnotes' in blueprint.data &&
      'elementOrder' in blueprint.data
    ) {
      const newData = blueprint.data as {
        sections: Record<string, unknown>;
        devnotes: Record<string, unknown>;
        elementOrder: string[];
      };

      console.log("convertBlueprintToForm: Processing sections:", newData.sections);
      console.log("convertBlueprintToForm: elementOrder:", newData.elementOrder);

      // Convert sections
      Object.entries(newData.sections).forEach(
        ([sectionKey, sectionData], sectionIndex) => {
          console.log(`convertBlueprintToForm: Processing section ${sectionKey}:`, sectionData);
          
          // Check if sectionKey is an ID (UUID format) or a title
          const isSectionId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sectionKey);
          const sectionId = isSectionId ? sectionKey : `section-${sectionIndex + 1}`;
          
          // Use _sectionTitle if available, otherwise fall back to sectionKey or title
          const sectionTitle = (sectionData as any)?._sectionTitle || 
            (isSectionId ? (sectionData as any)?.title || 'Untitled Section' : sectionKey);
          
          console.log(`convertBlueprintToForm: Section ${sectionKey} - ID: ${sectionId}, Title: ${sectionTitle}`);
          
          const fields: Array<{
            id: string;
            type: 'text' | 'number' | 'image' | 'glossary-item' | 'tags' | 'types';
            label: string;
            mandatory: boolean;
            translation: boolean;
            selectedTypes?: string[];
            selectedTags?: string[];
          }> = [];

          if (
            sectionData &&
            typeof sectionData === 'object' &&
            'type' in sectionData
          ) {
            // This is a single field section (has direct type property)
            console.log(`convertBlueprintToForm: Processing single field section: ${sectionKey}`);
            const config = sectionData as {
              req: boolean;
              type: string;
              trans: boolean;
              selectedTypes?: string[];
              selectedTags?: string[];
            };

            let fieldType:
              | 'text'
              | 'number'
              | 'image'
              | 'glossary-item'
              | 'tags'
              | 'types' = 'text';
            if (config.type === 'number') {
              fieldType = 'number';
            } else if (config.type === 'image') {
              fieldType = 'image';
            } else if (config.type === 'glossary-item') {
              fieldType = 'glossary-item';
            } else if (config.type === 'tags') {
              fieldType = 'tags';
            } else if (config.type === 'types') {
              fieldType = 'types';
            }

            const field = {
              id: config.id || `${sectionId}-1`,
              type: fieldType,
              label: config.label || sectionTitle,
              mandatory: config.req || false,
              translation: config.trans || false,
              ...(fieldType === 'types' &&
                config.selectedTypes && {
                  selectedTypes: config.selectedTypes,
                }),
              ...(fieldType === 'glossary-item' &&
                config.selectedTags && {
                  selectedTags: config.selectedTags,
                }),
            };
            
            console.log(`convertBlueprintToForm: Created field for single field section:`, field);
            fields.push(field);
          } else if (sectionData && typeof sectionData === 'object') {
            // This is a section with multiple fields
            console.log(`convertBlueprintToForm: Processing multi-field section: ${sectionKey}`);
            Object.entries(sectionData).forEach(
              ([fieldKey, fieldConfig], fieldIndex) => {
                // Skip the _fieldOrder property
                if (fieldKey === '_fieldOrder') return;
                
                if (
                  fieldConfig &&
                  typeof fieldConfig === 'object' &&
                  'type' in fieldConfig
                ) {
                  const config = fieldConfig as {
                    req: boolean;
                    type: string;
                    trans: boolean;
                    selectedTypes?: string[];
                    selectedTags?: string[];
                  };

                  let fieldType:
                    | 'text'
                    | 'number'
                    | 'image'
                    | 'glossary-item'
                    | 'tags'
                    | 'types' = 'text';
                  if (config.type === 'number') {
                    fieldType = 'number';
                  } else if (config.type === 'image') {
                    fieldType = 'image';
                  } else if (config.type === 'glossary-item') {
                    fieldType = 'glossary-item';
                  } else if (config.type === 'tags') {
                    fieldType = 'tags';
                  } else if (config.type === 'types') {
                    fieldType = 'types';
                  }

                  const field = {
                    id: config.id || `${sectionId}-${fieldIndex + 1}`,
                    type: fieldType,
                    label: config.label || fieldKey,
                    mandatory: config.req || false,
                    translation: config.trans || false,
                    ...(fieldType === 'types' &&
                      config.selectedTypes && {
                        selectedTypes: config.selectedTypes,
                      }),
                    ...(fieldType === 'glossary-item' &&
                      config.selectedTags && {
                        selectedTags: config.selectedTags,
                      }),
                  };
                  
                  console.log(`convertBlueprintToForm: Created field for multi-field section:`, field);
                  fields.push(field);
                }
              }
            );
          }

          console.log(`convertBlueprintToForm: Section ${sectionTitle} has ${fields.length} fields:`, fields);
          
          sections.push({
            id: sectionId,
            title: sectionTitle,
            fields,
          });
        }
      );

      // Convert developer notes
      Object.entries(newData.devnotes).forEach(([noteId, noteData]) => {
        if (noteData && typeof noteData === 'object' && 'note' in noteData) {
          const note = noteData as { color: string; note: string };

          // Convert hex color to our color system
          const hexToColor = (
            hex: string
          ): 'amber' | 'blue' | 'green' | 'red' | 'purple' | 'gray' => {
            const colorMap: Record<
              string,
              'amber' | 'blue' | 'green' | 'red' | 'purple' | 'gray'
            > = {
              '#f3f5a3': 'amber',
              '#a3f3f5': 'blue',
              '#a3f5a3': 'green',
              '#f5a3a3': 'red',
              '#f3a3f5': 'purple',
              '#a3a3a3': 'gray',
            };
            return colorMap[hex] || 'amber';
          };

          developerNotes.push({
            id: noteId,
            message: note.note,
            color: hexToColor(note.color),
          });
        }
      });

      // Build render order - handle UUID-based elementOrder properly
      console.log("convertBlueprintToForm: Building render order from elementOrder:", newData.elementOrder);
      newData.elementOrder.forEach((elementId) => {
        console.log(`convertBlueprintToForm: Processing element in order: ${elementId}`);
        if (elementId in newData.devnotes) {
          console.log(`convertBlueprintToForm: Adding note to render order: ${elementId}`);
          renderOrder.push({ type: 'note', id: elementId });
        } else if (elementId in newData.sections) {
          // Find the section with this ID
          const section = sections.find(s => s.id === elementId);
          if (section) {
            console.log(`convertBlueprintToForm: Adding section to render order: ${elementId} -> ${section.id}`);
            renderOrder.push({
              type: 'section',
              id: section.id,
            });
          } else {
            console.log(`convertBlueprintToForm: Section not found for element: ${elementId}`);
          }
        } else {
          console.log(`convertBlueprintToForm: Element not found in sections or devnotes: ${elementId}`);
        }
      });
    }
  }

  console.log("convertBlueprintToForm: Final result:", { sections, developerNotes, renderOrder });
  return { sections, developerNotes, renderOrder };
}

/**
 * Decodes HTML entities and renders HTML content safely
 * @param html - HTML string that may contain entities like &amp;, &lt;, &gt;, etc.
 * @returns Decoded HTML string
 */
export function decodeHtml(html: string): string {
  if (!html) return "";
  
  // Create a temporary div element to decode HTML entities
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.innerHTML;
}

/**
 * Safely renders HTML content by decoding entities and using dangerouslySetInnerHTML
 * @param html - HTML string that may contain entities
 * @returns Object with dangerouslySetInnerHTML property
 */
export function renderHtml(html: string): { dangerouslySetInnerHTML: { __html: string } } {
  return {
    dangerouslySetInnerHTML: { __html: decodeHtml(html) }
  };
}
